import { Component, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  BusinessPartnerCaregiverTeamResponseDto,
  BusinessPartnerResponseDto,
  BusinessPartnerType,
} from '@pflegix/types';
import {
  AppIconService,
  EntityHeaderBaseDataComponent,
  IconWithTextComponent,
  PfxTemplateDirective,
  ScrollPanelComponent,
} from '@pflegix/ui';
import { AddressUtilService, DateTimeUtilService } from '@pflegix/util';
import { firstValueFrom } from 'rxjs';
import { RatingSummaryComponent } from '../../rating-management/rating-summary/rating-summary.component';
import { BusinessPartnerStore } from '../store';
import { isDependent, isIndividual, isInternalCaregiver } from '../util';

@Component({
  selector: 'pfx-business-partner-header-data',
  imports: [
    EntityHeaderBaseDataComponent,
    IconWithTextComponent,
    ScrollPanelComponent,
    PfxTemplateDirective,
    RatingSummaryComponent,
    RouterLink,
  ],
  templateUrl: './business-partner-header-data.component.html',
})
export class BusinessPartnerHeaderDataComponent implements OnInit {
  public businessPartnerId = input.required<string>();

  public showRatings = input<boolean>(true);

  private businessPartnerStore = inject(BusinessPartnerStore);

  private businessPartner = computed<BusinessPartnerResponseDto | undefined>(
    () => this.businessPartnerStore.entityMap()[this.businessPartnerId() ?? '']
  );

  protected address = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner?.mainAddress.street && businessPartner.mainAddress.city && businessPartner.mainAddress.zip
      ? AddressUtilService.generateFormattedAddressFromAddress(businessPartner.mainAddress)
      : undefined;
  });

  protected addressLink = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner?.mainAddress.street && businessPartner.mainAddress.city && businessPartner.mainAddress.zip
      ? `https://www.google.com/maps/place/${AddressUtilService.generateFormattedAddressURLFromAddress(businessPartner.mainAddress)}`
      : undefined;
  });

  protected dateOfBirth = computed(() => {
    const businessPartner = this.businessPartner();

    if (!businessPartner || !isIndividual(businessPartner) || !businessPartner.legalInformation.dateOfBirth) {
      return undefined;
    }

    return DateTimeUtilService.formatDateOfBirth(businessPartner.legalInformation.dateOfBirth);
  });

  protected phone = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? businessPartner.mainAddress.phone?.value : '';
  });

  protected mobile = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? businessPartner.mainAddress.mobile?.value : '';
  });

  protected email = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner ? businessPartner.mainAddress.email : '';
  });

  protected surveyLink = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner
      ? `https://docs.google.com/forms/d/e/1FAIpQLScVKwEQ5pcrQXfmQgyFd0DC_7-lCgOp_9nXaVYVz3_yzzDMvw/viewform?usp=pp_url&entry.879531967=${
          businessPartner.mainAddress.lastname
        }&entry.485428648=${businessPartner.mainAddress.firstname}&entry.90850396=${businessPartner._id}`
      : '';
  });

  protected notes = computed(() => this.businessPartner()?.notes);

  protected notesWithSeparator = computed(() => {
    const notes = this.notes();

    return notes ? notes.replace(/\n/g, ' | ') : '';
  });

  protected isIndividual = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner && isIndividual(businessPartner);
  });

  protected isInternalCaregiver = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner && isInternalCaregiver(businessPartner);
  });

  protected isDependent = computed(() => {
    const businessPartner = this.businessPartner();

    return businessPartner && isDependent(businessPartner);
  });

  private readonly appIconService = inject(AppIconService);

  protected team = signal<BusinessPartnerCaregiverTeamResponseDto | undefined>(undefined);

  protected icons = {
    phone: this.appIconService.getCommonIcon('phone').icon,
    mobile: this.appIconService.getCommonIcon('mobile').icon,
    email: this.appIconService.getCommonIcon('email').icon,
    address: this.appIconService.getCommonIcon('address').icon,
    dateOfBirth: this.appIconService.getCommonIcon('calendar').icon,
    notes: this.appIconService.getCommonIcon('notes').icon,
    team: this.appIconService.getBusinessPartnerIconByType(BusinessPartnerType.CAREGIVER_TEAM).icon,
    rating: this.appIconService.getRatingIcon().icon,
  };

  constructor() {
    effect(() => {
      this.loadTeam();
    });
  }

  public ngOnInit(): void {
    this.businessPartnerStore.loadById(this.businessPartnerId());
  }

  private async loadTeam(): Promise<void> {
    const businessPartner = this.businessPartner();

    if (businessPartner && isInternalCaregiver(businessPartner)) {
      const team = await firstValueFrom(this.businessPartnerStore.getById(businessPartner.partners.team ?? ''));

      this.team.set(team as BusinessPartnerCaregiverTeamResponseDto);
    }
  }
}
